import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';

import { Confirm, Icons, Modal } from '@components/common';
import PageTitle from '@components/common/PageTitle';
import Table from '@components/common/Table';
import {
	TAssignApprovalUnitsFormSubmitHandler,
} from '@components/routes/AdminPanel/AssignApprovalUnits/AssignApprovalUnitsForm/AssignApprovalUnitsForm';

import { useMutateCreateAssigmentApprovalUnit } from '@hooks/assets/useMutateCreateAssigmentApprovalUnit';
import { useMutateDeleteAssignApprovalUnit } from '@hooks/assets/useMutateDeleteAssignApprovalUnit';
import { useMutateUpdateAssignmentApprovalUnit } from '@hooks/assets/useMutateUpdateAssignmentApprovalUnit';
import { useQueryAssignedApprovalUnits } from '@hooks/assets/useQueryAssignedApprovalUnits';
import { useQueryDropdownFilterApprovalUnits } from '@hooks/assets/useQueryDropdownFilterApprovalUnits';

import { assignApprovalUnit } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setPage, setPageSize, setSearchValue, setSort, setUnitId } from '@slices/assignApprovalUnit';

import { setFilterUtil } from '@utils/filterUtil';

import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	ISortBy,
	ITablePagination,
	ITableSearch,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';
import { TAssignmentUnit } from '@interfaces/organisation/assignApprovalUnits';
import { ICreateApprovalUnitDTO } from '@interfaces/organisation/dto/approvalUnitsDTO';

import { EAdminPanelPageTitles } from '@enums/common';

import { MODAL_WIDTH_480 } from '@constants/modals';
import { emptyText } from './constants';

import AssignApprovalUnitsForm from './AssignApprovalUnitsForm';
import { getColumns } from './columns';

import styles from './AssignApprovalUnits.module.scss';

const AssignApprovalUnits = () => {
	const dispatch = useAppDispatch();

	const [isOpenDeleteFormModal, setIsOpenDeleteFormModal] = useState(false);
	const [isOpenAssignmentFormModal, setIsOpenAssignmentFormModal] = useState(false);
	const [isDirtyFormModal, setIsDirtyFormModal] = useState(false);
	const [editAssignment, setEditAssignment] = useState<TAssignmentUnit | null>(null);
	const [deleteAssignmentId, setDeleteAssignmentId] = useState<string | null>(null);
	const [selectedUnit, setSelectedUnit] = useState<TSelectOption | null>(null);

	const toggleDeleteModal = () => setIsOpenDeleteFormModal((prevState) => !prevState);
	const toggleUnitFormModal = () => setIsOpenAssignmentFormModal((prevState) => !prevState);
	const toggleAddModal = useCallback(() => {
		setEditAssignment(null);
		toggleUnitFormModal();
	}, []);

	const closeAddConfirm = () => setIsOpenAssignmentFormModal(false);
	const closeDeleteConfirm = () => {
		setIsOpenDeleteFormModal(false);
		setDeleteAssignmentId(null);
	};

	const onSearchValue = (searchValue: string) => dispatch(setSearchValue({ searchValue }));

	const addUnitHandler = (data: ICreateApprovalUnitDTO) => {
		if (editAssignment?.id) handleUpdateAssignApprovalUnit({ data, unitId: editAssignment.id });
		else handleCreateAssignApprovalUnit(data);
	};

	const deleteUnitHandler = () => {
		if (deleteAssignmentId) handleDeleteAssignApprovalUnit({ unitId: deleteAssignmentId });

		closeDeleteConfirm();
		setDeleteAssignmentId(null);
	};

	const {
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
		unitId,
		assetType,
		assetSubType,
	} = useAppSelector(assignApprovalUnit);

	const {
		approvalAssignUnitData,
		countOfRecords,
		isLoadingApprovalUnit,
	} = useQueryAssignedApprovalUnits({
		page,
		pageSize,
		searchValue,
		sortDirection,
		sortFieldName,
		unitId,
		assetType,
		assetSubType,
		closeAddModal: closeAddConfirm,
	});
	const {
		handleCreateAssignApprovalUnit,
		isLoadingCreateAssignApprovalUnit,
	} = useMutateCreateAssigmentApprovalUnit([closeAddConfirm]);
	const { dropdownFilterApprovalUnits } = useQueryDropdownFilterApprovalUnits();
	const { handleDeleteAssignApprovalUnit } = useMutateDeleteAssignApprovalUnit([closeAddConfirm]);
	const {
		handleUpdateAssignApprovalUnit,
		isLoadingUpdateAssignApprovalUnit,
	} = useMutateUpdateAssignmentApprovalUnit([closeAddConfirm]);
	const handleSort = (sortBy: Array<ISortBy>) =>
		dispatch(setSort({
			sortDirection: !!sortBy[0]?.desc,
			sortFieldName: sortBy[0]?.id,
		}));

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedUnit,
			currentValue: unitId,
			setAction: setUnitId,
			fieldName: 'unitId',
			dispatch,
		});
	}, [selectedUnit, unitId, dispatch]);

	const sort: ITableSort = {
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	};

	const search: Array<ITableSearch> = [
		{
			initialValue: '',
			name: 'name',
			handler: onSearchValue,
			tooltipText: 'Search by asset type and subtype',
		},
	];

	const filters: Array<TTableFilter> = useMemo(() => [
		{
			name: 'unitId',
			placeholder: 'Approval Unit',
			option: selectedUnit,
			options: dropdownFilterApprovalUnits,
			minWidth: '200px',
			setState: setSelectedUnit,
			defaultValue: unitId,
		},
	], [dropdownFilterApprovalUnits, selectedUnit, unitId]);

	const pagination: ITablePagination = useMemo(() => ({
		countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler: (page: number) => dispatch(setPage({ page })),
		pageSizeHandler: (pageSize: number) => dispatch(setPageSize({ pageSize })),
	}), [countOfRecords, pageSize, page, dispatch]);
	const columns =
		getColumns({
			setDeleteAssignmentId,
			setEditAssignment,
			toggleDeleteModal,
			toggleEditModal: toggleUnitFormModal,
		});

	return (
		<div className={styles.manageAssignApprovalUnitsContainer}>
			<PageTitle
				icon={Icons.assetsRequestsLight()}
				title={EAdminPanelPageTitles.ApprovalUnitAssignment}
			/>
			<Modal
				isOpen={isOpenDeleteFormModal}
				onClose={closeDeleteConfirm}
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onClose={closeDeleteConfirm}
					onSubmit={deleteUnitHandler}
					question='Are you sure you want to remove all Approval Units?'
				/>
			</Modal>
			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyFormModal}
				isOpen={isOpenAssignmentFormModal}
				onClose={closeAddConfirm}
				title='Assign Approval unit to the Asset type'
				width='600px'
			>
				<AssignApprovalUnitsForm
					editAssignment={editAssignment}
					isLoadingSubmit={isLoadingUpdateAssignApprovalUnit || isLoadingCreateAssignApprovalUnit}
					onClose={closeAddConfirm}
					setIsDirtyFormModal={setIsDirtyFormModal}
					submitHandler={addUnitHandler as TAssignApprovalUnitsFormSubmitHandler}
				/>
			</Modal>
			<div
				className={styles.assignUnitsApprovalListTableContainer}
				data-testid='manage-assign-approval-units-table'
			>
				<Table
					addTooltipText='Add assigned Approval units'
					columns={columns}
					data={approvalAssignUnitData}
					emptyText={emptyText}
					filters={filters}
					isCreatable
					isEmptyTextCenter
					isLoading={isLoadingApprovalUnit}
					isWithoutCheckbox
					isWithoutDeleteAction
					pagination={pagination}
					search={search}
					searchThreshold={0}
					sort={sort}
					toggleAddModal={toggleAddModal}
				/>
			</div>
		</div>
	);
};

export default memo(AssignApprovalUnits);
